/**
 * SupportTicketList — the cross-lab triage inbox: a filterable, server-paginated table of support tickets
 * with each row's labo→admin unread badge. Selecting a row drives the chat panel (controlled selection,
 * lifted to the screen). The inbox refreshes on the shell hook's short staleTime AND whenever a chat
 * send / mark-read invalidates the management root, so the unread badges track the open chat near-real-
 * time. Full 5-state DoD (forbidden/offline are handled by the parent screen; here we cover
 * loading/error/empty over the table).
 *
 * Accessibility: search + the two select filters carry real labels; the selected row is marked
 * aria-current; the "open" action names the ticket reference. AA contrast via tokens; the unread count is
 * a badge with text + glyph (never color-only).
 */
import { useMemo } from "react";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Input,
  LoadingState,
  Pagination,
  Select,
  Table,
  type Column,
} from "@/ui";
import { useT } from "@/app";
import { useSupportTickets } from "../api";
import { SUPPORT_LIVE_POLL_MS } from "../support.api";
import { ticketStatutTone } from "../lib";
import { categorieLabelKey, prioriteGlyph, prioriteLabelKey, prioriteTone, statutLabelKey } from "../support.lib";
import type { SupportFilters, SupportTicketRow, TicketPriorite, TicketStatut } from "../types";

const STATUTS: ReadonlyArray<TicketStatut> = ["ouvert", "en_cours", "resolu", "ferme"];
const PRIORITES: ReadonlyArray<TicketPriorite> = ["urgente", "haute", "normale", "basse"];

export interface SupportTicketListProps {
  filters: SupportFilters;
  onFiltersChange: (next: SupportFilters) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Gate (super-admin && online) — also gates the inbox poll. */
  enabled: boolean;
}

export function SupportTicketList({
  filters,
  onFiltersChange,
  selectedId,
  onSelect,
  enabled,
}: SupportTicketListProps) {
  const { t } = useT();
  // Refreshes via the shell hook's staleTime + the management-root invalidation a chat write triggers,
  // so the unread badges track the open chat near-real-time.
  const query = useSupportTickets(filters, enabled);

  /**
   * Patch the filter set, DELETING any key whose new value is empty (rather than setting it to
   * `undefined`) so the resulting object satisfies `exactOptionalPropertyTypes`. Resets to page 1 on a
   * filter change unless the patch itself carries a `page`. The patch type explicitly allows `undefined`
   * values (a cleared select) — those keys are stripped.
   */
  function patchFilters(patch: { [K in keyof SupportFilters]?: SupportFilters[K] | undefined }): void {
    const next: Record<string, unknown> = { ...filters, page: 1, ...patch };
    Object.keys(next).forEach((k) => {
      if (next[k] === undefined || next[k] === "") delete next[k];
    });
    onFiltersChange(next as SupportFilters);
  }

  const rows = useMemo(() => query.data?.items ?? [], [query.data]);
  const page = query.data?.page ?? 1;
  const pageCount = query.data?.totalPages ?? 1;

  const columns: ReadonlyArray<Column<SupportTicketRow>> = [
    {
      key: "reference",
      header: t("mgmt.support.col.reference"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{r.reference}</span>,
    },
    {
      key: "sujet",
      header: t("mgmt.support.col.sujet"),
      cell: (r) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
          <span>{r.sujet}</span>
          <Badge tone="neutral" glyph="▣">
            {t(categorieLabelKey(r.categorie))}
          </Badge>
        </div>
      ),
    },
    {
      key: "statut",
      header: t("mgmt.support.col.statut"),
      cell: (r) => (
        <Badge tone={ticketStatutTone(r.statut)} glyph="●">
          {t(statutLabelKey(r.statut))}
        </Badge>
      ),
    },
    {
      key: "priorite",
      header: t("mgmt.support.col.priorite"),
      cell: (r) => (
        <Badge tone={prioriteTone(r.priorite)} glyph={prioriteGlyph(r.priorite)}>
          {t(prioriteLabelKey(r.priorite))}
        </Badge>
      ),
    },
    {
      key: "unread",
      header: t("mgmt.support.col.unread"),
      align: "end",
      cell: (r) =>
        r.unread && r.unread > 0 ? (
          <Badge tone="warn" glyph="✦" aria-label={t("mgmt.support.unread.aria", { n: r.unread })}>
            {r.unread}
          </Badge>
        ) : (
          <span aria-hidden style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>
            —
          </span>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "end",
      cell: (r) => (
        <Button
          size="sm"
          variant={selectedId === r.id ? "primary" : "secondary"}
          aria-current={selectedId === r.id ? "true" : undefined}
          aria-label={t("mgmt.support.open", { ref: r.reference })}
          onClick={() => onSelect(r.id)}
        >
          {t("mgmt.support.open", { ref: r.reference })}
        </Button>
      ),
    },
  ];

  return (
    <Card padded={false} title={t("mgmt.support.inbox.title")}>
      <div
        style={{
          display: "flex",
          gap: "var(--nv-s-2)",
          flexWrap: "wrap",
          padding: "var(--nv-s-3)",
          borderBottom: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
        }}
      >
        <Input
          value={filters.q ?? ""}
          onChange={(e) => patchFilters({ q: e.target.value })}
          placeholder={t("mgmt.support.inbox.search")}
          aria-label={t("mgmt.support.inbox.search")}
          style={{ flex: "1 1 14rem" }}
        />
        <Select
          aria-label={t("mgmt.support.filter.statut")}
          placeholder={t("mgmt.support.filter.all")}
          value={filters.statut ?? ""}
          onChange={(e) => patchFilters({ statut: (e.target.value || undefined) as TicketStatut | undefined })}
          options={STATUTS.map((s) => ({ value: s, label: t(statutLabelKey(s)) }))}
        />
        <Select
          aria-label={t("mgmt.support.filter.priorite")}
          placeholder={t("mgmt.support.filter.all")}
          value={filters.priorite ?? ""}
          onChange={(e) => patchFilters({ priorite: (e.target.value || undefined) as TicketPriorite | undefined })}
          options={PRIORITES.map((p) => ({ value: p, label: t(prioriteLabelKey(p)) }))}
        />
      </div>

      <div style={{ padding: "var(--nv-s-3)" }}>
        {query.isLoading ? (
          <LoadingState variant="table" rows={6} />
        ) : query.isError ? (
          <ErrorState
            message={query.error.message}
            code={query.error.code}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <Table
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              caption={t("mgmt.support.inbox.title")}
              selectedKey={selectedId}
              onRowClick={(r) => onSelect(r.id)}
              emptyTitle={t("mgmt.support.inbox.empty.title")}
              emptyDescription={t("mgmt.support.inbox.empty.desc")}
            />
            {pageCount > 1 && (
              <div style={{ marginBlockStart: "var(--nv-s-3)" }}>
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  {...(query.data?.total !== undefined ? { total: query.data.total } : {})}
                  onPageChange={(p) => patchFilters({ page: p })}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/** The default inbox poll cadence re-exported so the screen can reference it in a hint. */
export { SUPPORT_LIVE_POLL_MS };
